import { TFunction } from 'i18next';
import { IntegrationConfigBase } from 'models/integrations/integrationConfigBase';
import { IIntegrationEvidence } from 'models/integrations/integrationEvidence';
import { toLocaleDateTime } from 'utils/datetime';

//The API's GitHub_MFAHandler limits
export const gitHubMaxExemptAccounts = 50;
export const gitHubMaxExemptReasonLength = 200;

const loginPattern = /^[A-Za-z0-9](?:[A-Za-z0-9-]{0,38})$/;

export const isGitHubLogin = (login: string): boolean => loginPattern.test(login);

//An account that may go without secure 2FA (a bot, a service account), with the reason why
export class GitHubExemptAccount {
  login: string = '';

  reason: string = '';

  constructor(login?: string, reason?: string) {
    this.login = login ?? '';
    this.reason = reason ?? '';
  }

  isEqual(other: GitHubExemptAccount): boolean {
    return this.login.toLowerCase() === other.login.toLowerCase() && this.reason.trim() === other.reason.trim();
  }
}

//
// Config class: the API's GitHub_MFAConfig
//
export class IntegrationConfig_GitHub_MFA extends IntegrationConfigBase {
  requireOrganizationEnforcement: boolean = true;

  failOnInsecureMethod: boolean = true;

  includeOutsideCollaborators: boolean = true;

  exemptAccounts: GitHubExemptAccount[] = [];

  createChecklistItemsPerUser: boolean = false;

  static from(stored: unknown): IntegrationConfig_GitHub_MFA {
    const raw = (stored ?? {}) as Omit<Partial<IntegrationConfig_GitHub_MFA>, 'exemptAccounts'> & {
      exemptAccounts?: unknown;
    };
    const config = new IntegrationConfig_GitHub_MFA();
    config.requireOrganizationEnforcement = raw.requireOrganizationEnforcement ?? true;
    config.failOnInsecureMethod = raw.failOnInsecureMethod ?? true;
    config.includeOutsideCollaborators = raw.includeOutsideCollaborators ?? true;
    config.exemptAccounts = Array.isArray(raw.exemptAccounts)
      ? raw.exemptAccounts.map((item: Partial<GitHubExemptAccount> | null) => {
          const login = typeof item?.login === 'string' ? item.login : '';
          const reason = typeof item?.reason === 'string' ? item.reason : '';

          return new GitHubExemptAccount(login, reason);
        })
      : [];
    config.createChecklistItemsPerUser = raw.createChecklistItemsPerUser ?? false;

    return config;
  }

  clone(): IntegrationConfig_GitHub_MFA {
    return IntegrationConfig_GitHub_MFA.from(this);
  }

  //the exemption of a login, compared case-insensitively
  getExemption(login: string): GitHubExemptAccount | undefined {
    return this.exemptAccounts.find((account) => account.login.toLowerCase() === login.toLowerCase());
  }

  //exempts the logins that are not exempted yet, all with this reason
  addExemptions(logins: string[], reason: string): void {
    for (const login of logins) {
      if (!this.getExemption(login)) this.exemptAccounts.push(new GitHubExemptAccount(login, reason.trim()));
    }
  }

  removeExemptions(logins: string[]): void {
    const names = new Set(logins.map((login) => login.toLowerCase()));
    this.exemptAccounts = this.exemptAccounts.filter((account) => !names.has(account.login.toLowerCase()));
  }

  //the exemptions compared as a set: the order does not change the outcome
  isEqual(other: IntegrationConfig_GitHub_MFA): boolean {
    return (
      this.requireOrganizationEnforcement === other.requireOrganizationEnforcement &&
      this.failOnInsecureMethod === other.failOnInsecureMethod &&
      this.includeOutsideCollaborators === other.includeOutsideCollaborators &&
      this.createChecklistItemsPerUser === other.createChecklistItemsPerUser &&
      this.exemptAccounts.length === other.exemptAccounts.length &&
      this.exemptAccounts.every((account) => {
        const match = other.getExemption(account.login);

        return match !== undefined && account.isEqual(match);
      })
    );
  }

  //The problems of the API's Validate; empty when valid
  getProblems(t: TFunction<string[]>): string[] {
    const problems: string[] = [];

    if (this.exemptAccounts.length > gitHubMaxExemptAccounts) {
      problems.push(t('integrations:GitHub_MFA.Problems.TooManyExemptions', { max: gitHubMaxExemptAccounts }));
    }

    const invalid = this.exemptAccounts.filter((account) => !isGitHubLogin(account.login));
    if (invalid.length > 0) {
      problems.push(
        t('integrations:GitHub_MFA.Problems.InvalidLogin', { logins: invalid.map((a) => a.login).join(', ') }),
      );
    }

    const logins = this.exemptAccounts.map((account) => account.login.toLowerCase());
    if (logins.some((login, index) => logins.indexOf(login) !== index)) {
      problems.push(t('integrations:GitHub_MFA.Problems.DuplicateExemption'));
    }

    if (this.exemptAccounts.some((account) => !account.reason.trim())) {
      problems.push(t('integrations:GitHub_MFA.Problems.ReasonRequired'));
    }

    if (this.exemptAccounts.some((account) => account.reason.length > gitHubMaxExemptReasonLength)) {
      problems.push(t('integrations:GitHub_MFA.Problems.ReasonTooLong', { max: gitHubMaxExemptReasonLength }));
    }

    return problems;
  }
}

//The API's account types and 2FA states
export enum GitHubMfaAccountTypes {
  Owner = 'Owner',
  Member = 'Member',
  OutsideCollaborator = 'OutsideCollaborator',
}

export enum GitHubTwoFactorStates {
  Ok = 'Ok',
  Disabled = 'Disabled',
  Insecure = 'Insecure',
}

export const getGitHubMfaAccountTypeText = (type: string, t: TFunction<string[]>): string => {
  switch (type) {
    case GitHubMfaAccountTypes.Owner:
      return t('integrations:GitHub_MFA.Type.Owner');
    case GitHubMfaAccountTypes.Member:
      return t('integrations:GitHub_MFA.Type.Member');
    case GitHubMfaAccountTypes.OutsideCollaborator:
      return t('integrations:GitHub_MFA.Type.OutsideCollaborator');
    default:
      return type;
  }
};

export const getGitHubTwoFactorText = (state: string, t: TFunction<string[]>): string => {
  switch (state) {
    case GitHubTwoFactorStates.Ok:
      return t('integrations:GitHub_MFA.TwoFactor.Ok');
    case GitHubTwoFactorStates.Disabled:
      return t('integrations:GitHub_MFA.TwoFactor.Disabled');
    case GitHubTwoFactorStates.Insecure:
      return t('integrations:GitHub_MFA.TwoFactor.Insecure');
    default:
      return state;
  }
};

//The organization setting "Require two-factor authentication": on, off, or unknown when the token could not read it
export const getGitHubEnforcementText = (value: boolean | undefined, t: TFunction<string[]>): string => {
  if (value === undefined) return t('integrations:GitHub_MFA.Enforcement.Unknown');

  return value ? t('integrations:GitHub_MFA.Enforcement.On') : t('integrations:GitHub_MFA.Enforcement.Off');
};

//
// Evidence classes
//
export class GitHubMfaAccount {
  login: string = '';

  id: number = 0;

  //Owner, Member or OutsideCollaborator
  type: string = '';

  //Ok, Disabled or Insecure
  twoFactor: string = GitHubTwoFactorStates.Ok;

  //the exemption's reason; undefined for an account that was not exempted
  reason: string | undefined = undefined;

  //without secure 2FA and not exempted
  isFinding(): boolean {
    return this.twoFactor !== GitHubTwoFactorStates.Ok && this.reason === undefined;
  }

  //without secure 2FA, and passing only through its exemption; an exempted account with secure 2FA simply complies
  isExempted(): boolean {
    return this.twoFactor !== GitHubTwoFactorStates.Ok && this.reason !== undefined;
  }
}

export class GitHub_MFAEvidence implements IIntegrationEvidence {
  organization: string = '';

  evaluatedAtUtc: Date = new Date();

  //undefined when the token could not see the setting
  organizationRequiresTwoFactor: boolean | undefined = undefined;

  totalMembers: number = 0;

  totalOutsideCollaborators: number = 0;

  totalFindingsAfterExemptions: number = 0;

  totalAccountsExempted: number = 0;

  pass: boolean = false;

  //the review list holds the first 500 accounts only, or a listing was cut off
  isCapped: boolean = false;

  //the settings this outcome was judged by; undefined in a result without them
  config: IntegrationConfig_GitHub_MFA | undefined = undefined;

  findings: GitHubMfaAccount[] = [];

  exemptions: GitHubMfaAccount[] = [];

  //findings first, then the exempted, then the accounts that are fine
  accounts: GitHubMfaAccount[] = [];

  toMarkdownReport(t: TFunction<['translation', 'integrations']>): string {
    const typedT = t as TFunction<string[]>;
    const sb: string[] = [];

    sb.push(
      `## ${t('integrations:GitHub_MFA.Report.SummaryHeading', {
        organization: this.organization,
        date: toLocaleDateTime(this.evaluatedAtUtc),
      })}`,
    );
    sb.push(
      `- ${t('integrations:GitHub_MFA.Result.Counts', {
        members: this.totalMembers,
        outside: this.totalOutsideCollaborators,
        findings: this.totalFindingsAfterExemptions,
        exempted: this.totalAccountsExempted,
      })}`,
    );
    sb.push(
      `- ${t('integrations:GitHub_MFA.Result.EnforcementLine', {
        value: getGitHubEnforcementText(this.organizationRequiresTwoFactor, typedT),
      })}`,
    );
    if (this.isCapped) {
      sb.push(`- ${t('integrations:GitHub_MFA.Result.Capped')}`);
    }
    sb.push(
      `- ${t('integrations:GitHubBaseline.Report.OutcomeLine', {
        outcome: this.pass
          ? t('integrations:GitHubBaseline.Result.Pass')
          : t('integrations:GitHubBaseline.Result.Fail'),
      })}`,
    );
    sb.push('');
    sb.push(
      `| ${t('integrations:GitHub_MFA.Columns.Account')} | ${t('integrations:GitHub_MFA.Columns.Type')} | ${t(
        'integrations:GitHub_MFA.Columns.TwoFactor',
      )} | ${t('integrations:GitHub_MFA.Columns.Exempt')} | ${t('integrations:GitHubBaseline.Columns.Result')} |`,
    );
    sb.push('| --- | --- | --- | --- | --- |');
    for (const account of this.findings.concat(this.exemptions)) {
      sb.push(
        `| ${account.login} | ${getGitHubMfaAccountTypeText(account.type, typedT)} | ${getGitHubTwoFactorText(
          account.twoFactor,
          typedT,
        )} | ${account.reason ?? ''} | ${
          account.isFinding()
            ? t('integrations:GitHubBaseline.Result.Fail')
            : account.isExempted()
            ? t('integrations:GitHub_MFA.Result.Exempted')
            : t('integrations:GitHubBaseline.Result.Pass')
        } |`,
      );
    }

    return sb.join('\n');
  }
}

const readAccounts = (raw: unknown): GitHubMfaAccount[] =>
  Array.isArray(raw)
    ? raw.map((item: Record<string, unknown>) => {
        const account = new GitHubMfaAccount();
        account.login = typeof item.login === 'string' ? item.login : '';
        account.id = typeof item.id === 'number' ? item.id : 0;
        account.type = typeof item.type === 'string' ? item.type : '';
        account.twoFactor = typeof item.twoFactor === 'string' ? item.twoFactor : GitHubTwoFactorStates.Ok;
        account.reason = typeof item.reason === 'string' ? item.reason : undefined;

        return account;
      })
    : [];

export const parse_Evidence_GitHub_MFA = (raw: string | undefined): GitHub_MFAEvidence | undefined => {
  if (!raw) return undefined;

  const rawEvidence = JSON.parse(raw);
  if (!rawEvidence) return undefined;

  const evidence = new GitHub_MFAEvidence();
  evidence.organization = rawEvidence.organization ?? '';
  evidence.evaluatedAtUtc = rawEvidence.evaluatedAtUtc ? new Date(rawEvidence.evaluatedAtUtc) : new Date();
  evidence.organizationRequiresTwoFactor =
    typeof rawEvidence.organizationRequiresTwoFactor === 'boolean'
      ? rawEvidence.organizationRequiresTwoFactor
      : undefined;
  evidence.totalMembers = rawEvidence.totalMembers ?? 0;
  evidence.totalOutsideCollaborators = rawEvidence.totalOutsideCollaborators ?? 0;
  evidence.totalFindingsAfterExemptions = rawEvidence.totalFindingsAfterExemptions ?? 0;
  evidence.totalAccountsExempted = rawEvidence.totalAccountsExempted ?? 0;
  evidence.pass = rawEvidence.pass ?? false;
  evidence.isCapped = rawEvidence.isCapped ?? false;
  evidence.config = rawEvidence.config ? IntegrationConfig_GitHub_MFA.from(rawEvidence.config) : undefined;
  evidence.findings = readAccounts(rawEvidence.findings);
  evidence.exemptions = readAccounts(rawEvidence.exemptions);
  evidence.accounts = readAccounts(rawEvidence.accounts);

  return evidence;
};
